/**
 * SoleadLab 2.0 - Upload Schema Modal
 *
 * Drag-and-drop JSON file upload with auto-detect preview.
 * Calls the WebWorker parser then uploads to backend.
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import Modal from '@/components/common/Modal';
import Button from '@/components/common/Button';
import { useMonitoringStore } from '@/stores';
import { parseJsonSchema } from '@/services/schemaParser';
import * as api from '@/services/monitoringApi';
import type { ParsedSchema } from '@/types/monitoring';
import './UploadSchemaModal.css';

interface UploadSchemaModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function UploadSchemaModal({ isOpen, onClose }: UploadSchemaModalProps) {
  const selectedSiteId = useMonitoringStore((s) => s.selectedSiteId);
  const setSchema = useMonitoringStore((s) => s.setSchema);

  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [parsedSchema, setParsedSchema] = useState<(ParsedSchema & { stats?: { totalKeys: number; entitiesFound: number; metricsFound: number; durationMs: number } }) | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Clear all state when modal opens (fixes stale error from previous session)
  useEffect(() => {
    if (isOpen) {
      setError(null);
      setParsedSchema(null);
      setIsProcessing(false);
      setProgress(0);
      setIsSaving(false);
    }
  }, [isOpen]);

  const processFile = useCallback(async (file: File) => {
    setError(null);
    setParsedSchema(null);
    setIsProcessing(true);
    setProgress(0);

    try {
      const text = await file.text();
      const json = JSON.parse(text);

      const result = await parseJsonSchema(json, {
        onProgress: (p) => setProgress(p),
      });

      setParsedSchema(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to parse JSON file');
    } finally {
      setIsProcessing(false);
    }
  }, []);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file && file.name.endsWith('.json')) {
      processFile(file);
    } else {
      setError('Please drop a .json file');
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      processFile(file);
    }
  };

  const handleUpload = async () => {
    if (!parsedSchema || !selectedSiteId) return;

    setIsSaving(true);
    try {
      await api.uploadSchema(selectedSiteId, parsedSchema);
      setSchema(parsedSchema);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload schema');
    } finally {
      setIsSaving(false);
    }
  };

  // Count entities by type
  const entityCounts = parsedSchema
    ? parsedSchema.entities.reduce<Record<string, number>>((acc, e) => {
        acc[e.entity_type] = (acc[e.entity_type] || 0) + 1;
        return acc;
      }, {})
    : {};

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Upload Schema"
      size="md"
      footer={
        <div className="usm-footer">
          <Button variant="ghost" onClick={onClose}>
            {parsedSchema ? 'Skip for now' : 'Cancel'}
          </Button>
          <Button
            variant="primary"
            onClick={handleUpload}
            disabled={!parsedSchema || isSaving}
            loading={isSaving}
          >
            Upload Schema
          </Button>
        </div>
      }
    >
      <div className="usm-content">
        <p className="usm-description">
          Upload a JSON file containing measurement data to extract the entity schema.
          The parser will auto-detect the structure.
        </p>

        {/* Drop Zone */}
        <div
          className={`usm-dropzone ${isDragging ? 'usm-dropzone--active' : ''} ${parsedSchema ? 'usm-dropzone--done' : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          {isProcessing ? (
            <div className="usm-dropzone__processing">
              <div className="usm-progress-bar">
                <div className="usm-progress-bar__fill" style={{ width: `${progress}%` }} />
              </div>
              <span>Parsing JSON...</span>
            </div>
          ) : (
            <>
              <svg className="usm-dropzone__icon" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
              <span className="usm-dropzone__text">
                Drop JSON file here or <strong>click to browse</strong>
              </span>
              <span className="usm-dropzone__hint">Supports measurement data JSON files</span>
            </>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept=".json"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
        </div>

        {/* Error */}
        {error && (
          <div className="usm-error">
            <span className="usm-error__icon">!</span>
            <span>{error}</span>
          </div>
        )}

        {/* Preview */}
        {parsedSchema && (
          <div className="usm-preview">
            <div className="usm-preview__header">
              <span className="usm-preview__check">&#10003;</span>
              <span className="usm-preview__title">Schema Preview</span>
            </div>

            {parsedSchema.detectedProvider && (
              <div className="usm-preview__provider">
                Detected provider: <strong>{parsedSchema.detectedProvider}</strong>
                <span className="usm-preview__confidence">
                  ({Math.round(parsedSchema.confidence * 100)}% confidence)
                </span>
              </div>
            )}

            <div className="usm-preview__stats">
              Found <strong>{parsedSchema.entities.length}</strong> entities:
            </div>

            <ul className="usm-preview__list">
              {Object.entries(entityCounts)
                .sort(([, a], [, b]) => b - a)
                .map(([type, count]) => (
                  <li key={type}>
                    <span className="usm-preview__type">{type}</span>
                    <span className="usm-preview__count">{count}</span>
                  </li>
                ))}
            </ul>

            {parsedSchema.stats && (
              <div className="usm-preview__meta">
                {parsedSchema.stats.metricsFound} metrics total &middot;{' '}
                parsed in {parsedSchema.stats.durationMs.toFixed(0)}ms
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}
